import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { Message } from "primereact/message";

import { Dialog, DialogHeader, Icon, SelectableCard } from "@/ds";

import { ConflitoIgnorarDialog } from "../conflito-ignorar-dialog";
import { ConflitoPassos } from "../conflito-passos";
import { TagSimulacao } from "../simulacao/tag-simulacao";
import { EscoposFornecedorStep } from "./components/escopos-fornecedor-step";
import { RevisaoFornecedorStep } from "./components/revisao-fornecedor-step";
import { SubstitutoFornecedorStep } from "./components/substituto-fornecedor-step";
import {
  CLASSIFICACAO_LABEL,
  COMO_RESOLVER_LABEL,
  CONFLITO_LOAD_ERROR_MESSAGE,
  QUAL_CONFLITO_LABEL,
  RECOMENDADO_LABEL,
  STEP_ESCOPOS_LABEL,
  STEP_FORNECEDOR_LABEL,
} from "./resolver-conflito-fornecedor.constants";
import styles from "../../conflito.module.scss";
import type {
  ConflitoFornecedorResumoModel,
  ResolverConflitoFornecedorDialogViewModel,
} from "./types-resolver-conflito-fornecedor";

function ConflitoResumo({ resumo }: { resumo: ConflitoFornecedorResumoModel }) {
  return (
    <section className={styles.resumo} data-testid="resolver-conflito-resumo">
      <div className={styles.resumoPessoa}>
        <span className={styles.resumoNome}>{resumo.nome}</span>
        <span className={styles.resumoPapel} data-testid="resolver-conflito-classificacao">
          {CLASSIFICACAO_LABEL}
        </span>
      </div>
      <span className={styles.resumoAlerta}>
        <Icon name="exclamation-triangle" size={10.5} />
        {resumo.alerta}
      </span>
      {resumo.obras.length > 0 ? (
        <ul className={styles.resumoObras}>
          {resumo.obras.map((obra) => (
            <li key={obra.key} className={styles.resumoObra} data-testid="resolver-conflito-obra">
              <span className={styles.resumoObraNome}>{obra.titulo}</span>
              <span>{obra.intervalo}</span>
            </li>
          ))}
        </ul>
      ) : null}
      {resumo.sobreposicao ? (
        <span className={styles.sobreposicao} data-testid="resolver-conflito-sobreposicao">
          <i className="pi pi-times-circle" aria-hidden />
          {resumo.sobreposicao}
        </span>
      ) : null}
    </section>
  );
}

export function ResolverConflitoFornecedorDialogView({
  etapa,
  titulo,
  orientacao,
  simulando,
  isSubmitting,
  semConflito,
  serverError,
  resumo,
  escolha,
  proposta,
  escopos,
  substituto,
  revisao,
  corpoRef,
  erroRef,
  ignorar,
  rodape,
  fechar,
}: ResolverConflitoFornecedorDialogViewModel) {
  if (ignorar) return <ConflitoIgnorarDialog {...ignorar} />;

  const { voltar, primario } = rodape;
  const passo = escopos ? "selecionar" : substituto ? "intermediario" : revisao ? "revisao" : null;

  return (
    <Dialog
      visible
      size={etapa === "revisao" ? "xl" : "md"}
      onHide={fechar}
      locked={isSubmitting}
      header={
        <DialogHeader
          title={
            <>
              {titulo}
              {simulando ? <TagSimulacao quebraNoMobile /> : null}
            </>
          }
          subtitle={orientacao ?? undefined}
        />
      }
      footer={
        <div className={styles.footer}>
          {voltar ? (
            <Button
              type="button"
              label="Voltar"
              severity="secondary"
              text
              onClick={voltar}
              disabled={isSubmitting}
              data-testid="resolver-conflito-voltar"
            />
          ) : (
            <Button
              type="button"
              label="Cancelar"
              severity="secondary"
              text
              onClick={fechar}
              disabled={isSubmitting}
              data-testid="resolver-conflito-cancel"
            />
          )}
          {primario ? (
            <Button
              type="button"
              label={primario.rotulo}
              onClick={primario.acionar}
              loading={primario.carregando}
              disabled={primario.desabilitado}
              data-testid={primario.testId}
            />
          ) : null}
        </div>
      }
      data-testid="resolver-conflito-dialog"
    >
      <div ref={corpoRef} className={styles.body}>
        {serverError ? (
          <div ref={erroRef}>
            <Message severity="error" text={serverError} data-testid="resolver-conflito-error" />
          </div>
        ) : null}
        {semConflito ? (
          <Message
            severity="error"
            text={CONFLITO_LOAD_ERROR_MESSAGE}
            data-testid="resolver-conflito-load-error"
          />
        ) : null}

        {resumo && etapa === "conflito" ? (
          <>
            <ConflitoResumo resumo={resumo} />
            <fieldset className={styles.secao}>
              <legend className={styles.secaoTitulo}>{QUAL_CONFLITO_LABEL}</legend>
              <div className={styles.opcoes}>
                {escolha.opcoes.map((opcao, index) => (
                  <SelectableCard
                    key={opcao.key}
                    value={index}
                    selected={opcao.marcada}
                    name="resolver-conflito-escolha"
                    onSelect={escolha.escolher}
                    data-testid="resolver-conflito-escolha"
                  >
                    <span className={styles.opcaoCorpo}>
                      <span className={styles.opcaoTitulo}>{opcao.titulo}</span>
                      <span className={styles.opcaoDescricao}>{opcao.descricao}</span>
                    </span>
                  </SelectableCard>
                ))}
              </div>
            </fieldset>
          </>
        ) : null}

        {resumo && etapa === "proposta" ? (
          <>
            <ConflitoResumo resumo={resumo} />
            <fieldset className={styles.secao} aria-describedby="resolver-conflito-forma-error">
              <legend className={styles.secaoTitulo}>{COMO_RESOLVER_LABEL}</legend>
              <div className={styles.opcoes}>
                {proposta.opcoes.map((opcao) => (
                  <SelectableCard
                    key={opcao.value}
                    value={opcao.value}
                    selected={opcao.marcada}
                    name="resolver-conflito-tratamento"
                    onSelect={proposta.escolher}
                    className={proposta.erro ? styles.invalida : undefined}
                    data-testid={opcao.testId}
                  >
                    <span className={styles.opcaoCorpo}>
                      <span className={styles.opcaoTituloLinha}>
                        <span className={styles.opcaoTitulo}>
                          <i className={opcao.icone} aria-hidden />
                          {opcao.label}
                        </span>
                        {opcao.recomendada ? (
                          <Tag className="p-tag-xs" severity="info" value={RECOMENDADO_LABEL} />
                        ) : null}
                      </span>
                      <span className={styles.opcaoDescricao}>{opcao.description}</span>
                    </span>
                  </SelectableCard>
                ))}
              </div>
              {proposta.erro ? (
                <span
                  id="resolver-conflito-forma-error"
                  className={styles.erro}
                  role="alert"
                  data-testid="resolver-conflito-forma-error"
                >
                  {proposta.erro}
                </span>
              ) : null}
            </fieldset>
          </>
        ) : null}

        {passo ? (
          <ConflitoPassos
            etapa={passo}
            selecionarLabel={STEP_ESCOPOS_LABEL}
            intermediarioLabel={STEP_FORNECEDOR_LABEL}
          />
        ) : null}
        {escopos ? <EscoposFornecedorStep {...escopos} /> : null}
        {substituto ? <SubstitutoFornecedorStep {...substituto} /> : null}
        {revisao ? <RevisaoFornecedorStep {...revisao} /> : null}
      </div>
    </Dialog>
  );
}
